import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useLocation, useNavigate } from "react-router-dom";
import { postDataWithBody, putDataWithBody, getData } from "../../../Services/Services";
import { getThemeURL, acknowledgeAndAssess, acknowledgeupdate } from "../../../Services/EndPoints";
import { Layers, MapPin, Building2, ShieldAlert, Calendar, UserCheck, Clock, CircleCheckBig, FileText, Activity, TriangleAlert, Globe, Radar, Phone, PhoneCall, CheckCircle2, Droplets, CloudRain, MountainSnow, Flame, Tornado, ClipboardList, Tag, Radio, Hash } from "lucide-react";
import { toast } from "react-toastify";

// Google Font Import
const FontImport = () => {
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return null;
};

// Animation Styles
const AnimationStyles = () => {
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes pulse-ring {
        0% {
          transform: scale(0.8);
          opacity: 1;
        }
        50% {
          transform: scale(1.2);
          opacity: 0.3;
        }
        100% {
          transform: scale(0.8);
          opacity: 1;
        }
      }
      
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      @keyframes checkmark {
        0% { stroke-dashoffset: 100; }
        100% { stroke-dashoffset: 0; }
      }

      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      .animate-slide-down {
        animation: slideDown 0.4s ease-out;
      }

      .animate-fade-in {
        animation: fadeIn 0.5s ease-out;
      }

      .animate-scale-in {
        animation: scaleIn 0.3s ease-out;
      }

      .animate-slide-in-right {
        animation: slideInRight 0.3s ease-out;
      }

      .pulse-dot {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: inherit;
        animation: pulse-ring 2s ease-out infinite;
      }

      /* ── DMS Header — Avalanche nav style ─────────────────────── */
      .lms__header {
        background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
        color: #fff !important;
        border-radius: 0 !important;
        padding: 0 28px !important;
        height: 56px !important;
        box-shadow: 0 2px 20px rgba(15,45,66,.18) !important;
        position: sticky !important;
        top: 0 !important;
        z-index: 100 !important;
      }
      .lms__title {
        font-family: 'Sora','Segoe UI',sans-serif !important;
        font-size: .88rem !important;
        font-weight: 700 !important;
        color: #fff !important;
        letter-spacing: -0.01em !important;
      }
      .lms__title svg {
        color: rgba(255,255,255,.8) !important;
        stroke: rgba(255,255,255,.8) !important;
      }
      .lms__title_icon {
        color: rgba(255,255,255,.8) !important;
      }
      .lms__title_icon svg,
      .lms__title_icon path {
        color: rgba(255,255,255,.8) !important;
        fill: rgba(255,255,255,.8) !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return null;
};

// Custom Layers Control Component
const CustomLayersControl = ({ activeLayer, onLayerChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const layers = [
    { id: 'road', name: 'Road View' },
    { id: 'satellite', name: 'Satellite View' },
    { id: 'terrain', name: 'Terrain View' },
    { id: 'hybrid', name: 'Hybrid View' },
  ];

  return (
    <div style={layersControlStyles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...layersControlStyles.button,
          backgroundColor: isOpen ? '#3B82F6' : 'white',
          color: isOpen ? 'white' : '#374151'
        }}
        title="Map Layers"
      >
        <Layers
          size={20}
          color={isOpen ? 'white' : '#374151'}
          strokeWidth={2.5}
        />
      </button>

      {isOpen && (
        <div style={layersControlStyles.panel} className="animate-slide-in-right">
          <div style={layersControlStyles.panelHeader}>
            <span style={layersControlStyles.panelTitle}>Map Layers</span>
            <button
              onClick={() => setIsOpen(false)}
              style={layersControlStyles.closeBtn}
            >
              ✕
            </button>
          </div>
          <div style={layersControlStyles.layersList}>
            {layers.map((layer) => (
              <div
                key={layer.id}
                onClick={() => {
                  onLayerChange(layer.id);
                  setIsOpen(false);
                }}
                style={{
                  ...layersControlStyles.layerItem,
                  backgroundColor: activeLayer === layer.id ? '#EFF6FF' : 'transparent',
                  borderLeft: activeLayer === layer.id ? '3px solid #3B82F6' : '3px solid transparent'
                }}
              >
                <div style={layersControlStyles.layerContent}>
                  <span style={{ fontSize: '16px' }}>{layer.icon}</span>
                  <span style={layersControlStyles.layerName}>{layer.name}</span>
                </div>
                {activeLayer === layer.id && (
                  <span style={{ color: '#3B82F6', fontSize: '14px' }}>✓</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Map Fullscreen Toggle Control
const MapFullscreenControl = ({ targetRef }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Give Leaflet a moment to see the new container size
      setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    const el = targetRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <button
      onClick={toggleFullscreen}
      title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
      style={{
        position: 'absolute',
        top: '58px',
        right: '10px',
        zIndex: 1000,
        width: 36,
        height: 36,
        borderRadius: 8,
        border: isFullscreen ? '2px solid #3b82f6' : '1px solid rgba(0,0,0,0.15)',
        background: isFullscreen ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isFullscreen ? '0 2px 12px rgba(59,130,246,0.35)' : '0 2px 6px rgba(0,0,0,0.15)',
        transition: 'all 0.2s ease',
        flexShrink: 0,
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
    >
      {isFullscreen ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
      )}
    </button>
  );
};

// Action Acknowledgment Modal
// Action Acknowledgment Modal
// Combined Acknowledge + Field Assessment Modal
const AcknowledgeAssessModal = ({
  isOpen, onClose, isSubmitted, disasterData, notificationData,
  assessmentData, handleAssessmentChange, onSubmitSuccess, submittedActionTaken
}) => {
  const [actionTaken, setActionTaken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submittedData, setSubmittedData] = useState(null);

  useEffect(() => {
    if (isSubmitted && notificationData?.actionTaken) {
      setActionTaken(notificationData.actionTaken);
    }
    if (isSubmitted && assessmentData) {
      setSubmittedData({ assessmentData, actionTaken: notificationData?.actionTaken || '' });
    }
  }, [isSubmitted, notificationData, assessmentData]);

  const formatDT = (val) => {
    if (!val) return 'NA';
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const dd = String(d.getDate()).padStart(2,'0');
    const mm = String(d.getMonth()+1).padStart(2,'0');
    const yy = d.getFullYear();
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2,'0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${dd}-${mm}-${yy}, ${String(h).padStart(2,'0')}:${min} ${ampm}`;
  };

  const isWarningLevel = notificationData?.severity === 'Level 0 – Warning';

  const handleSubmit = async () => {
    if (!actionTaken.trim()) { setError('Action Taken is required'); return; }
    setError('');
    setIsSubmitting(true);
    try {
      if (isWarningLevel) {
        // Level 0 – Warning: only acknowledge with actionTaken
        const endpoint = acknowledgeupdate.replace('/id/', `/${notificationData?.id}/`);
        const response = await putDataWithBody(endpoint, { actionTaken: actionTaken.trim() }, {});
        if (!response || response?.status !== 'success') throw new Error('API error');
        setSubmittedData({ assessmentData: {}, actionTaken: actionTaken.trim() });
        toast.success(response?.msg || 'Alert acknowledged successfully', {
          position: toast.POSITION.TOP_CENTER, autoClose: 2000
        });
        onSubmitSuccess(actionTaken.trim());
      } else {
        let createdByVal = 'Current_User';
        try {
          const profile = await getData(getThemeURL);
          if (profile?.[0]?.user_name) createdByVal = profile[0].user_name;
        } catch (_) {}

        const payload = {
          notificationId: notificationData?.id,
          actionTaken: actionTaken.trim(),
          assessment: {
            incidentId: null,
            alertId: disasterData?.id || '',
            hazardType: disasterData?.type || '',
            alertSource: notificationData?.sensorNetwork || 'System',
            alertTimestamp: disasterData?.reportedTime || new Date().toISOString(),
            incident_date: disasterData?.reportedTime || new Date().toISOString(),
            location: disasterData?.location || '',
            latitude: disasterData?.latitude,
            longitude: disasterData?.longitude,
            incidentStatus: assessmentData.incidentStatus,
            severityLevel: assessmentData.severityLevel,
            populationAffected: parseInt(assessmentData.populationAffected) || 0,
            fatalities: parseInt(assessmentData.fatalities) || 0,
            housesDamaged: parseInt(assessmentData.housesDamaged) || 0,
            roadsBlocked: assessmentData.roadsBlocked || '',
            bridgesDamaged: parseInt(assessmentData.bridgesDamaged) || 0,
            powerSupplyStatus: assessmentData.powerSupplyStatus,
            communicationStatus: assessmentData.communicationStatus,
            animalsLost: parseInt(assessmentData.animalsLost) || 0,
            animalShelterRequired: false,
            createdBy: createdByVal
          }
        };

        const response = await postDataWithBody(acknowledgeAndAssess, payload, {});
        if (response?.status === 'success' || response?.id || response?.notificationId) {
          setSubmittedData({ assessmentData: { ...assessmentData }, actionTaken: actionTaken.trim() });
          toast.success(response?.msg || 'Acknowledged & Assessment submitted successfully', {
            position: toast.POSITION.TOP_CENTER, autoClose: 2000
          });
          onSubmitSuccess(actionTaken.trim());
        } else {
          toast.error(response?.msg || 'Submission failed', {
            position: toast.POSITION.TOP_CENTER, autoClose: 2000
          });
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const roLabel = { fontSize: '11px', fontWeight: '600', color: '#7A9BB0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' };
  const roValue = { fontSize: '14px', fontWeight: '600', color: '#0F2D42', padding: '8px 12px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' };
  const inputStyle = { width: '100%', padding: '9px 12px', fontSize: '14px', border: '1px solid #CBD5E1', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', color: '#0F2D42' };
  const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', display: 'block' };
  const sectionTitle = { gridColumn: '1 / -1', fontSize: '12px', fontWeight: '700', color: '#18a3a0', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #E2E8F0', paddingBottom: '6px', marginTop: '8px' };

  const showReadOnly = isSubmitted || !!submittedData;
  const sd = submittedData?.assessmentData || assessmentData;
  const sdAction = submittedData?.actionTaken ?? submittedActionTaken ?? notificationData?.actionTaken ?? '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,45,66,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '860px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ background: '#1C4E72', borderRadius: '16px 16px 0 0', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardList size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>
                {showReadOnly
                  ? 'Submitted Report'
                  : isWarningLevel
                    ? 'Acknowledge Alert'
                    : 'Acknowledge & Incident Situation Assessment'}
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>
                {showReadOnly
                  ? 'Read-only — submitted details'
                  : isWarningLevel
                    ? 'Provide action taken to acknowledge this warning alert'
                    : 'Fill all details and submit to acknowledge the alert'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', width: 32, height: 32, cursor: 'pointer', color: '#fff', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

          {/* Read-only alert info row */}
          <div style={{ display: 'grid', gridTemplateColumns: isWarningLevel ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '20px', padding: '14px 16px', background: '#F0F9FF', borderRadius: '10px', border: '1px solid #BAE6FD' }}>
            <div>
              <div style={roLabel}>Alert Severity</div>
              <div style={{ ...roValue, color: getSeverityColor(disasterData?.severity), background: `${getSeverityColor(disasterData?.severity)}18`, border: `1px solid ${getSeverityColor(disasterData?.severity)}40`, fontWeight: '700' }}>{disasterData?.severity || 'N/A'}</div>
            </div>
            {!isWarningLevel && (
              <div>
                <div style={roLabel}>Incident Date &amp; Time</div>
                <div style={roValue}>{formatDT(disasterData?.reportedTime)}</div>
              </div>
            )}
          </div>

          {showReadOnly ? (
            /* READ-ONLY VIEW */
            isWarningLevel ? (
              /* Warning level read-only: only Action Taken */
              <div>
                <div style={roLabel}>Action Taken</div>
                <div style={{ ...roValue, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{sdAction || '—'}</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={sectionTitle}>Incident Details</div>
                <div><div style={roLabel}>Incident Status</div><div style={roValue}>{sd.incidentStatus}</div></div>

                <div style={sectionTitle}>Human Impact</div>
                <div><div style={roLabel}>Population Affected</div><div style={roValue}>{sd.populationAffected || '0'}</div></div>
                <div><div style={roLabel}>Fatalities</div><div style={roValue}>{sd.fatalities || '0'}</div></div>

                <div style={sectionTitle}>Infrastructure Damage</div>
                <div><div style={roLabel}>Houses Damaged</div><div style={roValue}>{sd.housesDamaged || '0'}</div></div>
                <div><div style={roLabel}>Roads Blocked / Damaged</div><div style={roValue}>{sd.roadsBlocked || 'None'}</div></div>
                <div><div style={roLabel}>Bridges Damaged</div><div style={roValue}>{sd.bridgesDamaged || '0'}</div></div>
                <div><div style={roLabel}>Power Supply Status</div><div style={roValue}>{sd.powerSupplyStatus || 'Normal'}</div></div>
                <div style={{ gridColumn: '1/-1' }}><div style={roLabel}>Communication Status</div><div style={roValue}>{sd.communicationStatus || 'Normal'}</div></div>

                <div style={sectionTitle}>Livestock Impact</div>
                <div><div style={roLabel}>Animals Lost</div><div style={roValue}>{sd.animalsLost || '0'}</div></div>

                <div style={sectionTitle}>Response</div>
                <div style={{ gridColumn: '1/-1' }}>
                  <div style={roLabel}>Action Taken</div>
                  <div style={{ ...roValue, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{sdAction || '—'}</div>
                </div>
              </div>
            )
          ) : isWarningLevel ? (
            /* Level 0 – Warning: only Action Taken field */
            <div>
              <label style={labelStyle}>Action Taken <span style={{ color: '#EF4444' }}>*</span></label>
              <textarea
                value={actionTaken}
                onChange={e => { setActionTaken(e.target.value); if (error) setError(''); }}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                placeholder="Describe actions taken in response to this warning alert..."
                maxLength={250}
                rows={5}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                {error && <div style={{ color: '#EF4444', fontSize: '12px' }}>{error}</div>}
                <div style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>{actionTaken.length}/250</div>
              </div>
            </div>
          ) : (
            /* EDITABLE FULL ASSESSMENT FORM */
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div style={{ ...sectionTitle, gridColumn: '1 / -1' }}>Incident Details</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Incident Status</label>
                <select name="incidentStatus" value={assessmentData.incidentStatus} onChange={handleAssessmentChange} style={inputStyle}>
                  <option value="Active">Active</option>
                  <option value="Under Control">Under Control</option>
                </select>
              </div>

              <div style={sectionTitle}>Human Impact</div>
              <div>
                <label style={labelStyle}>Population Affected</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5} name="populationAffected" value={assessmentData.populationAffected} onChange={handleAssessmentChange} style={inputStyle} placeholder="e.g. 2500" />
              </div>
              <div>
                <label style={labelStyle}>Fatalities</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} name="fatalities" value={assessmentData.fatalities} onChange={handleAssessmentChange} style={inputStyle} placeholder="e.g. 5" />
              </div>

              <div style={sectionTitle}>Infrastructure Damage</div>
              <div>
                <label style={labelStyle}>Houses Damaged</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} name="housesDamaged" value={assessmentData.housesDamaged} onChange={handleAssessmentChange} style={inputStyle} placeholder="e.g. 120" />
              </div>
              <div>
                <label style={labelStyle}>Roads Blocked / Damaged</label>
                <input type="text" maxLength={50} name="roadsBlocked" value={assessmentData.roadsBlocked} onChange={handleAssessmentChange} style={inputStyle} placeholder="e.g. Highway 44 closed" />
              </div>
              <div>
                <label style={labelStyle}>Bridges Damaged</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2} name="bridgesDamaged" value={assessmentData.bridgesDamaged} onChange={handleAssessmentChange} style={inputStyle} placeholder="e.g. 2" />
              </div>
              <div>
                <label style={labelStyle}>Power Supply Status</label>
                <select name="powerSupplyStatus" value={assessmentData.powerSupplyStatus} onChange={handleAssessmentChange} style={inputStyle}>
                  <option value="Normal">Normal</option>
                  <option value="Disrupted">Disrupted</option>
                  <option value="Down">Down</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Communication Status</label>
                <select name="communicationStatus" value={assessmentData.communicationStatus} onChange={handleAssessmentChange} style={inputStyle}>
                  <option value="Normal">Normal</option>
                  <option value="Disrupted">Disrupted</option>
                  <option value="Down">Down</option>
                </select>
              </div>

              <div style={sectionTitle}>Livestock Impact</div>
              <div>
                <label style={labelStyle}>Animals Lost</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} name="animalsLost" value={assessmentData.animalsLost} onChange={handleAssessmentChange} style={inputStyle} placeholder="e.g. 45" />
              </div>

              <div style={sectionTitle}>Response</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Action Taken <span style={{ color: '#EF4444' }}>*</span></label>
                <textarea
                  value={actionTaken}
                  onChange={e => { setActionTaken(e.target.value); if (error) setError(''); }}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
                  placeholder="Describe actions taken in response to this alert..."
                  maxLength={250}
                  rows={4}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  {error && <div style={{ color: '#EF4444', fontSize: '12px' }}>{error}</div>}
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>{actionTaken.length}/250</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: '12px', borderRadius: '0 0 16px 16px' }}>
          <button onClick={onClose} style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #CBD5E1', background: '#fff', color: '#475569', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
            Close
          </button>
          {!showReadOnly && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ padding: '10px 28px', borderRadius: '8px', border: 'none', background: isSubmitting ? '#94A3B8' : '#1C4E72', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {isSubmitting ? (
                <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Submitting...</>
              ) : (
                <><CheckCircle2 size={16} />{isWarningLevel ? 'Acknowledge' : 'Acknowledge & Submit Assessment'}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// Map Controller Component
const MapController = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);

  return null;
};

// Extract core severity name from "Level X – Name" or plain "Name" format
const getCoreSeverity = (severity) => {
  if (!severity) return '';
  const parts = severity.split('–');
  return parts.length > 1 ? parts[parts.length - 1].trim() : severity.trim();
};

// Get severity color — module-level so both AcknowledgeModal and AccidentViewer can use it
const getSeverityColor = (severity) => {
  const core = getCoreSeverity(severity);
  const colorMap = {
    'CRITICAL': '#8B0000',
    'Critical': '#8B0000',
    'HIGH': '#F44336',
    'High': '#F44336',
    'Major': '#F44336',
    'WARNING': '#FFC107',
    'Warning': '#FFC107',
    'MEDIUM': '#FF9800',
    'Medium': '#FF9800',
    'Moderate': '#FF9800',
    'LOW': '#4CAF50',
    'Low': '#4CAF50',
    'Minor': '#4CAF50'
  };
  return colorMap[core] || colorMap[severity] || '#94A3B8';
};

// Custom disaster marker icon
const createDisasterIcon = (type, severity) => {
  const icons = {
    FLOOD: { color: "#3B82F6", icon: <Droplets size={24} color="#FFFFFF" /> },
    CLOUDBURST: { color: "#0284c7", icon: <CloudRain size={24} color="#FFFFFF" /> },
    'CLOUD BURST': { color: "#0284c7", icon: <CloudRain size={24} color="#FFFFFF" /> },
    "Cloudburst": { color: "#0284c7", icon: <CloudRain size={24} color="#FFFFFF" /> },
    COLD_WAVE: { color: "#60A5FA", icon: <Droplets size={24} color="#FFFFFF" /> },
    "Cold Wave": { color: "#60A5FA", icon: <Droplets size={24} color="#FFFFFF" /> },
    EARTHQUAKE: { color: "#8B5CF6", icon: <Activity size={24} color="#FFFFFF" /> },
    Earthquake: { color: "#8B5CF6", icon: <Activity size={24} color="#FFFFFF" /> },
    Flood: { color: "#3B82F6", icon: <Droplets size={24} color="#FFFFFF" /> },
    Fire: { color: "#DC2626", icon: <Flame size={24} color="#FFFFFF" /> },
    Cyclone: { color: "#F59E0B", icon: <Tornado size={24} color="#FFFFFF" /> },
    "Heat Wave": { color: "#F97316", icon: <Flame size={24} color="#FFFFFF" /> },
    AVALANCHE: { color: "#9333EA", icon: <MountainSnow size={24} color="#FFFFFF" /> },
    Avalanche: { color: "#9333EA", icon: <MountainSnow size={24} color="#FFFFFF" /> },
    DEFAULT: { color: "#6B7280", icon: <TriangleAlert size={24} color="#FFFFFF" /> }
  };

  const disaster = icons[type] || icons.DEFAULT;
  const isCritical = getCoreSeverity(severity) === "Critical" || severity === "CRITICAL";

  return L.divIcon({
    html: `
      <div style="
        background: white;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: 3px solid ${disaster.color};
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        position: relative;
      ">
        ${isCritical ? '<div class="pulse-dot"></div>' : ''}
        <div style="
          background: ${disaster.color};
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          position: relative;
          z-index: 1;
        ">${renderToString(disaster.icon)}</div>
      </div>
    `,
    className: "disaster-marker",
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
};

// Convert notification data
const convertNotificationToDisaster = (notification) => {
  const typeMap = {
    "Flood": "FLOOD",
    // "Cold Wave": "COLD_WAVE",
    "Cloudburst": "CLOUDBURST",
    "Cloud Burst": "CLOUDBURST",  // ADD THIS LINE
    "CLOUD BURST": "CLOUDBURST",   // ADD THIS LINE
    "Earthquake": "EARTHQUAKE",
    "Fire": "FIRE",
    "Cyclone": "CYCLONE",
    "Heat Wave": "HEAT_WAVE",
    "Avalanche": "AVALANCHE"
  };

  return {
    id: notification?.caseId || `DMC-${notification?.id || '448'}`,
    // type: typeMap[notification?.disasterType] || notification?.disasterType?.toUpperCase() || "COLD_WAVE",
    type: typeMap[notification?.disasterType] || notification?.disasterType?.toUpperCase() || "CLOUDBURST", // Changed default to CLOUDBURST
    title: notification?.notificationTitle || `${notification?.disasterType}`,
    description: notification?.alertDescription || notification?.notificationMessage || "Cold Wave (Critical) in Drass, Ladakh - Value: -25.0°C",
    latitude: notification?.latitude || 34.1526,
    longitude: notification?.longitude || 77.5771,
    severity: notification?.severity || notification?.priority || "CRITICAL",
    location: notification?.location || "Drass, Ladakh",
    acknowledgedName: notification?.acknowledgedName,
    affectedArea: notification?.affectedArea || "N/A",
    affectedPopulation: notification?.affectedPopulation || "N/A",
    reportedTime: notification?.createdAt ? new Date(notification?.createdAt).toISOString() : new Date().toISOString(),
    reportedBy: notification?.reportedBy || "Disaster Management System",
    acktime: notification?.acktime || notification?.acknowledgedTimestamp || null,
    contact: notification?.contact || "+91 1982-xxxxx",
    actionTaken: notification?.actionTaken,
    assessments: notification?.assessments || [],
    measuredValue: notification?.measuredValue || "-25.0°C",
    thresholdValue: notification?.thresholdValue || "-20.0°C",
    aboveThreshold: notification?.aboveThreshold || "-6°C (Critical!!)",
    unit: notification?.unit || "°C",
    // Only present on alerts created via the Create Alert form's Sensor
    // source (see CreateAlert.js's buildAlertJson) — undefined/null for
    // anything else, so the "Manual Alert Details" section that reads
    // these stays hidden for those (see hasManualAlertDetails below).
    sourceType: notification?.sourceType || null,
    rawAlertTitle: notification?.rawAlertTitle || null,
    sourceDetails: notification?.sourceDetails || null,
    onset: notification?.onset || null,
    effective: notification?.effective || null,
    expires: notification?.expires || null,
    regionLocation: notification?.regionLocation || null,
    emergencyContacts: [
      { name: "Emergency Helpline", number: "112" },
      { name: "Police Control", number: "100" },
      { name: "Ambulance", number: "108" }
    ]
  };
};

const DisasterManagementUI = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const notificationData = location.state?.notificationData;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allNotifications = location.state?.allNotifications || [];


  const [disasters, setDisasters] = useState([]);
  const [activeDisasterIndex, setActiveDisasterIndex] = useState(0);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [, setCurrentStatus] = useState("Pending");
  const [activeMapLayer, setActiveMapLayer] = useState('terrain');

  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [isAssessmentSubmitted, setIsAssessmentSubmitted] = useState(false);
  const [submittedActionTaken, setSubmittedActionTaken] = useState(null);
  const [assessmentData, setAssessmentData] = useState({
    incidentStatus: 'Active',
    severityLevel: 'Level 1 – Minor',
    populationAffected: '',
    fatalities: '',
    housesDamaged: '',
    roadsBlocked: '',
    bridgesDamaged: '',
    powerSupplyStatus: 'Normal',
    communicationStatus: 'Normal',
    animalsLost: '',
    animalShelterRequired: 'No'
  });

  const numericAssessmentFields = ['populationAffected', 'fatalities', 'housesDamaged', 'bridgesDamaged', 'animalsLost'];
  // Max length per assessment field — digits for the numeric ones,
  // characters for the free-text ones.
  const assessmentMaxLen = {
    populationAffected: 5,
    fatalities: 4,
    housesDamaged: 4,
    bridgesDamaged: 2,
    animalsLost: 3,
    roadsBlocked: 50,
  };
  const handleAssessmentChange = (e) => {
    const { name, value } = e.target;
    if (numericAssessmentFields.includes(name)) {
      let sanitized = value.replace(/[^0-9]/g, '');
      if (assessmentMaxLen[name]) sanitized = sanitized.slice(0, assessmentMaxLen[name]);
      setAssessmentData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }
    if (name === 'roadsBlocked') {
      if (value !== '' && !/^[a-zA-Z0-9\s/.,]*$/.test(value)) return;
      setAssessmentData(prev => ({ ...prev, [name]: value.slice(0, assessmentMaxLen.roadsBlocked) }));
      return;
    }
    setAssessmentData(prev => ({ ...prev, [name]: value }));
  };


  useEffect(() => {
    if (notificationData) {
      const convertedDisasters = allNotifications.length > 0
        ? allNotifications.map(convertNotificationToDisaster)
        : [convertNotificationToDisaster(notificationData)];

      setDisasters(convertedDisasters);

      const clickedIndex = allNotifications.findIndex(n => n.id === notificationData.id);
      setActiveDisasterIndex(clickedIndex >= 0 ? clickedIndex : 0);

      const isAlreadyAcknowledged = notificationData.acknowledged || false;
      setIsAcknowledged(isAlreadyAcknowledged);
      // setIsAssessmentSubmitted(isAlreadyAcknowledged);
      setCurrentStatus(isAlreadyAcknowledged ? "Acknowledged" : "Pending");


      // ── NEW: pre-fill assessment from existing data ──────────────
      const existingAssessments = notificationData.assessments || [];
      if (existingAssessments.length > 0) {
        const a = existingAssessments[0];
        setAssessmentData({
          incidentStatus: a.incidentStatus || 'Active',
          severityLevel: a.severityLevel || 'Level 1 – Minor',
          populationAffected: String(a.populationAffected ?? ''),
          fatalities: String(a.fatalities ?? ''),
          housesDamaged: String(a.housesDamaged ?? ''),
          roadsBlocked: a.roadsBlocked || '',
          bridgesDamaged: String(a.bridgesDamaged ?? ''),
          powerSupplyStatus: a.powerSupplyStatus || 'Normal',
          communicationStatus: a.communicationStatus || 'Normal',
          animalsLost: String(a.animalsLost ?? ''),
          animalShelterRequired: a.animalShelterRequired ? 'Yes' : 'No',
        });
        setIsAssessmentSubmitted(true);   // treat existing assessment as already submitted
      } else {
        setIsAssessmentSubmitted(isAlreadyAcknowledged);
      }
    } else {
      navigate('/Disaster-Acknowladgement');
    }
  }, [notificationData, allNotifications, navigate]);

  const disasterData = disasters[activeDisasterIndex] || convertNotificationToDisaster(notificationData || {});
  const [mapCenter] = useState([disasterData.latitude, disasterData.longitude]);
  const [mapZoom] = useState(12);
  const mapWrapperRef = useRef(null);
  console.log("dissatere", disasterData)


  const getTypeIcon = (type) => {
    const iconWrapper = (IconComponent, bgColor, iconColor) => (
      <div style={{
        width: '34px',
        height: '34px',
        borderRadius: '10px',
        backgroundColor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <IconComponent size={18} color={iconColor} />
      </div>
    );

    const typeUpper = type.toUpperCase();

    if (typeUpper.includes('FLOOD') || typeUpper.includes('WATER')) {
      return iconWrapper(Droplets, '#E3F2FD', '#1565C0');
    } else if (typeUpper.includes('CLOUD')) {
      return iconWrapper(CloudRain, '#F3E5F5', '#6A1B9A');
    } else if (typeUpper.includes('EARTHQUAKE')) {
      return iconWrapper(Activity, '#E0F2FE', '#0284C7'); // Blue theme as requested in image
    } else if (typeUpper.includes('FIRE') || typeUpper.includes('HEAT')) {
      return iconWrapper(Flame, '#FFEBEE', '#C62828');
    } else if (typeUpper.includes('CYCLONE')) {
      return iconWrapper(Tornado, '#E0F7FA', '#006064');
    } else if (typeUpper.includes('AVALANCHE')) {
      return iconWrapper(MountainSnow, '#E8F5E9', '#2E7D32'); // Green/Brown theme
    }

    return iconWrapper(TriangleAlert, '#FFF8E1', '#F57F17');
  };

  const getTileLayerUrl = (layerId) => {
    const urls = {
      road: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      satellite: 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      terrain: 'https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
      hybrid: 'https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
    };
    return urls[layerId] || urls.terrain;
  };

  const handleEmergencyCall = (number) => {
    window.open(`tel:${number}`, '_blank');
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "NA";

    let date;

    // Handle array format [year, month, day, hour, minute, second]
    if (Array.isArray(dateValue) && dateValue.length >= 5) {
      const [year, month, day, hour, minute, second = 0] = dateValue;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else {
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) return "NA";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear());

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, "0");

    return `${dd}-${mm}-${yy}, ${hours}:${minutes} ${ampm}`;
  };

  // SACHET_CAP / SACHET_EARTHQUAKE alerts come from NDMA's feed; SENSOR
  // alerts are the ones created manually via the Create Alert form. Any
  // other/missing sourceType hides the badge rather than guessing.
  const getAlertTypeBadge = (sourceType) => {
    if (sourceType === "SACHET_CAP" || sourceType === "SACHET_EARTHQUAKE") {
      return { label: "Third-Party API", color: "#C2410C" };
    }
    if (sourceType === "SENSOR") {
      return { label: "Sensor", color: "#2E6DA4" };
    }
    return null;
  };

  const getDisasterTypeLabel = (type) => {
    const labels = {
      'CLOUDBURST': 'Cloudburst',
      // 'CLOUDBURST': 'Cloudburst', // Changed from Cold Wave
      'Cloudburst': 'Cloudburst', // Changed from Cold Wave
      'AVALANCHE': 'Avalanche',
      'CLOUD BURST': 'Cloudburst',  // ADD THIS LINE
      'Cloud Burst': 'Cloudburst',
      'COLD_WAVE': 'Cold Wave',
      'Cold Wave': 'Cold Wave',
      'EARTHQUAKE': 'Earthquake',
      'Earthquake': 'Earthquake',
      'FLOOD': 'Flood',
      'Flood': 'Flood',
      'Fire': 'Fire',
      'Cyclone': 'Cyclone',
      'Heat Wave': 'Heat Wave',
      'Avalanche': 'Avalanche'
    };
    return labels[type] || type;
  };

  // Get trigger conditions based on disaster type and severity
  const getTriggerConditions = (type, severity) => {
    // Warning severity conditions
    const warningConditions = {
      'FLOOD': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Flood — Warning Conditions',
        items: [
          'Water level approaching threshold limits',
          'Noticeable rise in river or reservoir level',
          'Potential risk of downstream inundation',
          'Increased monitoring and preparedness advised'
        ]
      },
      'Flood': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Flood — Warning Conditions',
        items: [
          'Water level approaching threshold limits',
          'Noticeable rise in river or reservoir level',
          'Potential risk of downstream inundation',
          'Increased monitoring and preparedness advised'
        ]
      },
      'CLOUD BURST': {
        icon: <CloudRain size={18} color="#6A1B9A" />,
        title: 'Cloudburst — Trigger Conditions',
        items: [
          'Extreme rainfall intensity threshold exceeded',
          'Catastrophic flash flood imminent',
          'High risk to life and property in downstream areas',
          'Immediate evacuation to higher ground required'
        ]
      },
      'Cloud Burst': {
        icon: <CloudRain size={18} color="#6A1B9A" />,
        title: 'Cloudburst — Trigger Conditions',
        items: [
          'Extreme rainfall intensity threshold exceeded',
          'Catastrophic flash flood imminent',
          'High risk to life and property in downstream areas',
          'Immediate evacuation to higher ground required'
        ]
      },
      'COLD_WAVE': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Cold Wave — Warning Conditions',
        items: [
          'Significant drop in temperature nearing threshold',
          'Possible risk to vulnerable population groups',
          'Preventive measures and readiness recommended'
        ]
      },
      'Cold Wave': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Cold Wave — Warning Conditions',
        items: [
          'Significant drop in temperature nearing threshold',
          'Possible risk to vulnerable population groups',
          'Preventive measures and readiness recommended'
        ]
      },
      'EARTHQUAKE': {
        icon: <Activity size={18} color="#0284C7" />,
        title: 'Earthquake — Warning Conditions',
        items: [
          'Detectable seismic activity below critical threshold',
          'Possibility of minor structural impact',
          'Heightened vigilance and readiness suggested'
        ]
      },
      'Earthquake': {
        icon: <Activity size={18} color="#0284C7" />,
        title: 'Earthquake — Warning Conditions',
        items: [
          'Detectable seismic activity below critical threshold',
          'Possibility of minor structural impact',
          'Heightened vigilance and readiness suggested'
        ]
      },
      'Fire': {
        icon: <Flame size={18} color="#C62828" />,
        title: 'Fire — Warning Conditions',
        items: [
          'Fire activity detected below critical threshold',
          'Potential for spread identified',
          'Monitoring of life and property situation',
          'Precautionary measures recommended'
        ]
      },
      'Cyclone': {
        icon: <Tornado size={18} color="#006064" />,
        title: 'Cyclone — Warning Conditions',
        items: [
          'Wind speed approaching threshold limits',
          'Storm development monitored',
          'Potential risk to coastal areas',
          'Preparedness and alert status maintained'
        ]
      },
      'HEAT_WAVE': {
        icon: <Flame size={18} color="#C62828" />,
        title: 'Heat Wave — Warning Conditions',
        items: [
          'Temperature approaching threshold limits',
          'Heat index rising',
          'Possible health risk to vulnerable population',
          'Preventive cooling measures advised'
        ]
      },
      'Heat Wave': {
        icon: <Flame size={18} color="#C62828" />,
        title: 'Heat Wave — Warning Conditions',
        items: [
          'Temperature approaching threshold limits',
          'Heat index rising',
          'Possible health risk to vulnerable population',
          'Preventive cooling measures advised'
        ]
      },
      'AVALANCHE': {
        icon: <MountainSnow size={18} color="#2E7D32" />,
        title: 'Avalanche — Trigger Conditions',
        items: [
          'Snow accumulation threshold exceeded',
          'High probability of avalanche occurrence',
          'Extreme danger to life and property',
          'Immediate evacuation and area closure required'
        ]
      },
      'Avalanche': {
        icon: <MountainSnow size={18} color="#2E7D32" />,
        title: 'Avalanche — Trigger Conditions',
        items: [
          'Snow accumulation threshold exceeded',
          'High probability of avalanche occurrence',
          'Extreme danger to life and property',
          'Immediate evacuation and area closure required'
        ]
      },
      'DEFAULT': {
        icon: <TriangleAlert size={18} color="#F57F17" />,
        title: 'Alert — Warning Conditions',
        items: [
          'Threshold levels approaching',
          'Potential risk to affected areas',
          'Preparedness and monitoring advised'
        ]
      }
    };

    // Critical/High severity conditions
    const criticalConditions = {
      'FLOOD': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Flood — Trigger Conditions',
        items: [
          'Water level threshold exceeded',
          'Rapid rise in river or reservoir level',
          'High risk of downstream inundation',
          'Immediate response required'
        ]
      },
      'Flood': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Flood — Trigger Conditions',
        items: [
          'Water level threshold exceeded',
          'Rapid rise in river or reservoir level',
          'High risk of downstream inundation',
          'Immediate response required'
        ]
      },
      'CLOUDBURST': { // Changed from COLD_WAVE
        icon: <CloudRain size={18} color="#6A1B9A" />,
        title: 'Cloudburst — Trigger Conditions', // Changed from Cold Wave
        items: [
          'Extreme rainfall intensity threshold exceeded',
          'Catastrophic flash flood imminent',
          'High risk to life and property in downstream areas',
          'Immediate evacuation to higher ground required'
        ]
      },
      'Cloudburst': { // Changed from Cold Wave
        icon: <CloudRain size={18} color="#6A1B9A" />,
        title: 'Cloudburst — Trigger Conditions', // Changed from Cold Wave
        items: [
          'Extreme rainfall intensity threshold exceeded',
          'Catastrophic flash flood imminent',
          'High risk to life and property in downstream areas',
          'Immediate evacuation to higher ground required'
        ]
      },
      'COLD_WAVE': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Cold Wave — Trigger Conditions',
        items: [
          'Temperature drop threshold exceeded',
          'High risk to population',
          'Immediate relief action recommended'
        ]
      },
      'Cold Wave': {
        icon: <Droplets size={18} color="#1565C0" />,
        title: 'Cold Wave — Trigger Conditions',
        items: [
          'Temperature drop threshold exceeded',
          'High risk to population',
          'Immediate relief action recommended'
        ]
      },
      'EARTHQUAKE': {
        icon: <Activity size={18} color="#0284C7" />,
        title: 'Earthquake — Trigger Conditions',
        items: [
          'Seismic activity threshold exceeded',
          'High probability of structural impact',
          'Immediate emergency response required'
        ]
      },
      'Earthquake': {
        icon: <Activity size={18} color="#0284C7" />,
        title: 'Earthquake — Trigger Conditions',
        items: [
          'Seismic activity threshold exceeded',
          'High probability of structural impact',
          'Immediate emergency response required'
        ]
      },
      'Fire': {
        icon: <Flame size={18} color="#C62828" />,
        title: 'Fire — Trigger Conditions',
        items: [
          'Fire intensity threshold exceeded',
          'Rapid spread risk identified',
          'High risk to life and property',
          'Immediate evacuation may be required'
        ]
      },
      'Cyclone': {
        icon: <Tornado size={18} color="#006064" />,
        title: 'Cyclone — Trigger Conditions',
        items: [
          'Wind speed threshold exceeded',
          'Storm surge warning in effect',
          'High risk to coastal areas',
          'Immediate shelter-in-place advised'
        ]
      },
      'HEAT_WAVE': {
        icon: <Flame size={18} color="#C62828" />,
        title: 'Heat Wave — Trigger Conditions',
        items: [
          'Temperature threshold exceeded',
          'High heat index recorded',
          'Health risk to vulnerable population',
          'Immediate cooling measures required'
        ]
      },
      'Heat Wave': {
        icon: <Flame size={18} color="#C62828" />,
        title: 'Heat Wave — Trigger Conditions',
        items: [
          'Temperature threshold exceeded',
          'High heat index recorded',
          'Health risk to vulnerable population',
          'Immediate cooling measures required'
        ]
      },
      'AVALANCHE': {
        icon: <MountainSnow size={18} color="#2E7D32" />,
        title: 'Avalanche — Warning Conditions',
        items: [
          'Snow accumulation approaching threshold levels',
          'Weather conditions favorable for avalanche formation',
          'Risk of snow slide in steep terrain',
          'Enhanced monitoring and caution advised'
        ]
      },
      'DEFAULT': {
        icon: <TriangleAlert size={18} color="#F57F17" />,
        title: 'Alert — Trigger Conditions',
        items: [
          'Critical threshold exceeded',
          'High risk to affected areas',
          'Immediate action recommended'
        ]
      }
    };

    // Check if severity is Warning
    const isWarningSeverity = getCoreSeverity(severity) === 'Warning' || severity === 'WARNING';

    // Select appropriate conditions based on severity
    const conditions = isWarningSeverity ? warningConditions : criticalConditions;

    return conditions[type] || conditions.DEFAULT;
  };

  const calculatePercentage = (measured, threshold) => {
    // Simple percentage calculation - you can adjust this logic based on your needs
    const measuredNum = parseFloat(measured);
    const thresholdNum = parseFloat(threshold);

    if (isNaN(measuredNum) || isNaN(thresholdNum) || thresholdNum === 0) {
      return 50; // Default to 50% if calculation fails
    }

    return Math.min(100, Math.abs((measuredNum / thresholdNum) * 100));
  };

  // Check if severity is Warning to conditionally hide Incident Date and Time
  const isWarningSeverity = getCoreSeverity(disasterData.severity) === 'Warning' || disasterData.severity === 'WARNING';

  // Only alerts created via the Create Alert form's Sensor source carry
  // these fields (see convertNotificationToDisaster above) — the section
  // rendering them stays hidden for every other alert.
  const hasManualAlertDetails = Boolean(
    disasterData.rawAlertTitle || disasterData.sourceDetails || disasterData.onset ||
    disasterData.effective || disasterData.expires || disasterData.regionLocation
  );

  return (
    <>
      <FontImport />
      <AnimationStyles />
      <AcknowledgeAssessModal
        isOpen={isAssessmentModalOpen}
        onClose={() => setIsAssessmentModalOpen(false)}
        isSubmitted={isAcknowledged || isAssessmentSubmitted}
        disasterData={disasterData}
        notificationData={notificationData}
        assessmentData={assessmentData}
        handleAssessmentChange={handleAssessmentChange}
        submittedActionTaken={submittedActionTaken ?? notificationData?.actionTaken}
        onSubmitSuccess={(actionTaken) => {
          setSubmittedActionTaken(actionTaken);
          setIsAcknowledged(true);
          setIsAssessmentSubmitted(true);
          setCurrentStatus('Acknowledged');
          setIsAssessmentModalOpen(false);
          setTimeout(() => navigate('/Disaster-acknowledgereport'), 1500);
        }}
      />

      <div style={styles.container}>
        {/* Alert Banner */}
        <div style={{
          ...styles.bottomBar,
          animation: 'slideIn 0.6s ease-out 1s forwards',
          opacity: 0,
          background: `linear-gradient(135deg, ${getSeverityColor(disasterData.severity)}14 0%, ${getSeverityColor(disasterData.severity)}08 100%)`,
          border: `1px solid ${getSeverityColor(disasterData.severity)}2E`,
          borderLeft: `4px solid ${getSeverityColor(disasterData.severity)}`,
          color: getSeverityColor(disasterData.severity),
        }}>
          <div style={styles.bottomBarContent}>
            <div style={styles.bottomBarLeft}>
              <span style={{
                ...styles.bottomBarText,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#0F2D42',
                fontWeight: '600',
                animation: getCoreSeverity(disasterData.severity) === 'Critical' || disasterData.severity === 'CRITICAL' ? 'blink 1s infinite' : ''
              }}>
                <span style={{ fontSize: '15px', lineHeight: 1, flexShrink: 0 }}>🚨</span>
                Active {getDisasterTypeLabel(disasterData.type)} Alert
                <span style={{ color: '#94A3B8', margin: '0 2px' }}>•</span>
                <span style={{ color: '#475569', fontWeight: '500' }}>Hotline: {disasterData.contact}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Stacked Layout */}
        <div style={styles.stackedLayout} className="animate-fade-in">

          {/* ── SECTION 1: Full-width Alert Info Card ── */}
          <div style={styles.alertCard}>
            {/* Header with type icon, label, badges */}
            <div style={styles.alertCardHeader}>
              <div style={styles.alertHeaderLeft}>
                {getTypeIcon(disasterData.type)}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <span style={styles.alertTitle}>
                    {getDisasterTypeLabel(disasterData.type).toUpperCase()}
                  </span>
                  <div style={{ fontSize: '11.5px', color: '#8095A8', fontWeight: '600', letterSpacing: '0.01em' }}>
                    Alert ID: <span style={{ color: '#3A5F78' }}>{disasterData.id}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {getAlertTypeBadge(disasterData.sourceType) && (
                  <span style={{
                    ...styles.severityBadge,
                    backgroundColor: `${getAlertTypeBadge(disasterData.sourceType).color}18`,
                    color: getAlertTypeBadge(disasterData.sourceType).color,
                    border: `1px solid ${getAlertTypeBadge(disasterData.sourceType).color}30`,
                  }}>
                    Alert Type: {getAlertTypeBadge(disasterData.sourceType).label}
                  </span>
                )}
                <span style={{
                  ...styles.severityBadge,
                  backgroundColor: `${getSeverityColor(disasterData.severity)}18`,
                  color: getSeverityColor(disasterData.severity),
                  border: `1px solid ${getSeverityColor(disasterData.severity)}30`,
                }}>
                  {disasterData.severity}
                </span>
                {isAcknowledged && (
                  <span style={{
                    ...styles.severityBadge,
                    backgroundColor: '#E8F5E9',
                    color: '#2E7D32',
                    border: '1px solid #A5D6A7',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <CheckCircle2 size={12} /> ACKNOWLEDGED
                  </span>
                )}
              </div>
            </div>

            {/* 2-column info grid */}
            <div style={styles.infoGrid}>
              {/* Row 1 */}
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E8F5E9' }}>
                  <MapPin size={16} color="#2E7D32" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>REGION</div>
                  <div style={styles.infoItemValue}>{disasterData.location}</div>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E3F2FD' }}>
                  <Building2 size={16} color="#1565C0" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>REPORTED BY</div>
                  <div style={styles.infoItemValue}>{disasterData.reportedBy}</div>
                </div>
              </div>

              {/* Row 2 */}
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#FFF3E0' }}>
                  <ShieldAlert size={16} color="#E65100" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>SEVERITY</div>
                  <div style={{ ...styles.infoItemValue, color: getSeverityColor(disasterData.severity), fontWeight: '700' }}>
                    {disasterData.severity}
                  </div>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E3F2FD' }}>
                  <Calendar size={16} color="#1565C0" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>{isWarningSeverity ? 'WARNING DATE & TIME' : 'INCIDENT DATE & TIME'}</div>
                  <div style={styles.infoItemValue}>{formatDateTime(disasterData.reportedTime)}</div>
                </div>
              </div>

              {/* Row 3 */}
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E8F5E9' }}>
                  <UserCheck size={16} color="#2E7D32" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>ACKNOWLEDGED BY</div>
                  <div style={styles.infoItemValue}>{disasterData.acknowledgedName || 'N/A'}</div>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E3F2FD' }}>
                  <Clock size={16} color="#1565C0" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>ACKNOWLEDGE DATE & TIME</div>
                  <div style={styles.infoItemValue}>{formatDateTime(disasterData.acktime) || 'N/A'}</div>
                </div>
              </div>

              {/* Row 4 */}
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E8F5E9' }}>
                  <CircleCheckBig size={16} color="#2E7D32" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>ACTION TAKEN</div>
                  <div style={styles.infoItemValue}>{disasterData.actionTaken || 'N/A'}</div>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoItemIcon, backgroundColor: '#E3F2FD' }}>
                  <FileText size={16} color="#1565C0" />
                </div>
                <div>
                  <div style={styles.infoItemLabel}>DESCRIPTION</div>
                  <div style={styles.infoItemValue}>{disasterData.description || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── SECTION 1B: Manual Alert Details (only when present) ── */}
          {hasManualAlertDetails && (
            <div style={styles.card}>
              <div style={styles.cardHead}>
                <span style={styles.cardHeadIcon}><Tag size={13} color="#2E6DA4" /></span>
                <span style={styles.cardHeadTitle}>Manual Alert Details</span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.infoGrid}>
                  {[
                    { label: 'ALERT TITLE', raw: disasterData.rawAlertTitle, value: disasterData.rawAlertTitle, icon: Tag, bg: '#E8F5E9', color: '#2E7D32' },
                    { label: 'SENSOR TYPE', raw: disasterData.sourceDetails?.sensorType, value: disasterData.sourceDetails?.sensorType, icon: Radio, bg: '#E3F2FD', color: '#1565C0' },
                    { label: 'SENSOR NAME / ID', raw: disasterData.sourceDetails?.sensorNameId, value: disasterData.sourceDetails?.sensorNameId, icon: Hash, bg: '#E8F5E9', color: '#2E7D32' },
                    { label: 'LOCATION & SENSOR', raw: disasterData.regionLocation, value: disasterData.regionLocation, icon: MapPin, bg: '#E3F2FD', color: '#1565C0' },
                    { label: 'ALERT EFFECTIVE FROM', raw: disasterData.effective, value: formatDateTime(disasterData.effective), icon: Clock, bg: '#E3F2FD', color: '#1565C0' },
                    { label: 'ALERT VALID UNTIL', raw: disasterData.expires, value: formatDateTime(disasterData.expires), icon: Clock, bg: '#E8F5E9', color: '#2E7D32' },
                  ]
                    .filter((f) => f.raw)
                    .map((f) => (
                      <div key={f.label} style={styles.infoItem}>
                        <div style={{ ...styles.infoItemIcon, backgroundColor: f.bg }}>
                          <f.icon size={16} color={f.color} />
                        </div>
                        <div>
                          <div style={styles.infoItemLabel}>{f.label}</div>
                          <div style={styles.infoItemValue}>{f.value}</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* ── SECTION 2: Readings + Map (2-column) ── */}
          <div style={styles.twoColRow}>
            {/* Left: READINGS */}
            <div style={styles.card}>
              <div style={styles.cardHead}>
                <span style={styles.cardHeadIcon}><Activity size={13} color="#2E6DA4" /></span>
                <span style={styles.cardHeadTitle}>Readings</span>
              </div>
              <div style={styles.cardBody}>

                {/* Measured */}
                <div style={styles.measurementRow}>
                  <span style={styles.measurementLabel}><Activity size={14} color="#2A6591" style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Measured</span>
                  <span style={styles.measurementValue}>{disasterData.measuredValue} {disasterData.unit}</span>
                </div>
                <div style={styles.progressBarContainer}>
                  <div style={{
                    ...styles.progressBar,
                    width: `${calculatePercentage(disasterData.measuredValue, disasterData.thresholdValue)}%`,
                    backgroundColor: '#2A6591'
                  }} />
                </div>

                {/* Threshold */}
                <div style={styles.measurementRow}>
                  <span style={styles.measurementLabel}><TriangleAlert size={14} color="#E8A838" style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Threshold</span>
                  <span style={styles.measurementValue}>{disasterData.thresholdValue} {disasterData.unit}</span>
                </div>
                <div style={styles.progressBarContainer}>
                  <div style={{
                    ...styles.progressBar,
                    width: '75%',
                    backgroundColor: '#E8A838'
                  }} />
                </div>

                {/* Trigger Conditions */}
                <div style={styles.triggerConditionsSection}>
                  <div style={styles.triggerHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '8px', background: '#FFFFFF', border: '1px solid #F0E2C4', flexShrink: 0 }}>{getTriggerConditions(disasterData.type, disasterData.severity).icon}</div>
                    <span style={styles.triggerTitle}>{getTriggerConditions(disasterData.type, disasterData.severity).title}</span>
                  </div>
                  <div style={styles.triggerList}>
                    {getTriggerConditions(disasterData.type, disasterData.severity).items.map((condition, index) => (
                      <div key={index} style={styles.triggerItem}>
                        <span style={styles.triggerBullet} />
                        <span>{condition}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: DISASTER LOCATION */}
            <div style={styles.card}>
              <div style={styles.cardHead}>
                <span style={styles.cardHeadIcon}><MapPin size={13} color="#2E6DA4" /></span>
                <span style={styles.cardHeadTitle}>Disaster Location</span>
                <span style={{
                  marginLeft: 'auto',
                  padding: '4px 10px 4px 8px',
                  borderRadius: '20px',
                  fontSize: '10px',
                  fontWeight: '800',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: '#DC2626',
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#EF4444', boxShadow: '0 0 0 3px rgba(239,68,68,.18)', display: 'inline-block' }} /> Live
                </span>
              </div>
              <div style={styles.cardBody}>

              <div style={styles.mapContainer} ref={mapWrapperRef}>
                <MapContainer
                  center={mapCenter}
                  zoom={mapZoom}
                  minZoom={4}
                  attributionControl={false}
                  style={{ height: "100%", width: "100%", borderRadius: '8px' }}
                >
                  <MapController center={mapCenter} zoom={mapZoom} />
                  <TileLayer
                    key={activeMapLayer}
                    attribution={activeMapLayer === 'road' ? '&copy; OpenStreetMap contributors' : ''}
                    url={getTileLayerUrl(activeMapLayer)}
                    maxZoom={20}
                    subdomains={
                      activeMapLayer === 'road'
                        ? ['a', 'b', 'c']
                        : ['mt0', 'mt1', 'mt2', 'mt3']
                    }
                  />
                  <Marker
                    position={[disasterData.latitude, disasterData.longitude]}
                    icon={createDisasterIcon(disasterData.type, disasterData.severity)}
                  >
                    <Popup>
                      <div style={{ padding: '4px' }}>
                        <strong>{disasterData.location}</strong>
                        <p style={{ margin: '4px 0', fontSize: '12px' }}>{disasterData.severity} Alert</p>
                      </div>
                    </Popup>
                  </Marker>
                  <CircleMarker
                    center={[disasterData.latitude, disasterData.longitude]}
                    radius={100}
                    pathOptions={{
                      color: getSeverityColor(disasterData.severity),
                      fillColor: getSeverityColor(disasterData.severity),
                      fillOpacity: 0.1,
                      weight: 2
                    }}
                  />
                </MapContainer>
                <CustomLayersControl
                  activeLayer={activeMapLayer}
                  onLayerChange={setActiveMapLayer}
                />
                <MapFullscreenControl targetRef={mapWrapperRef} />
              </div>

              <div style={styles.mapInfo}>
                <div style={styles.mapInfoItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '9px', backgroundColor: '#EEF4FA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Globe size={15} color="#2E6DA4" />
                    </div>
                    <div>
                      <div style={styles.infoLabel}>COORDINATES</div>
                      <div style={styles.infoValue}>
                        {disasterData.latitude.toFixed(4)}°, {disasterData.longitude.toFixed(4)}°
                      </div>
                    </div>
                  </div>
                </div>
                <div style={styles.mapInfoItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '9px', backgroundColor: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Radar size={15} color="#E65100" />
                    </div>
                    <div>
                      <div style={styles.infoLabel}>AFFECTED RADIUS</div>
                      <div style={styles.infoValue}>~20 km</div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* ── SECTION 3: Emergency Contacts + Actions (2-column) ── */}
          <div style={styles.twoColRow}>
            {/* Left: EMERGENCY CONTACTS */}
            <div style={styles.card}>
              <div style={styles.cardHead}>
                <span style={styles.cardHeadIcon}><Phone size={13} color="#2E6DA4" /></span>
                <span style={styles.cardHeadTitle}>Emergency Contacts</span>
              </div>
              <div style={styles.cardBody}>
              <div style={styles.contactsGrid}>
                {[
                  { name: "Emergency Helpline", number: "112" },
                  { name: "Police Control", number: "100" },
                  { name: "Ambulance", number: "108" },
                  { name: "Fire Brigade", number: "101" }
                ].map((contact, index) => (
                  <div key={index} style={styles.contactCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '9px', backgroundColor: '#EEF4FA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Phone size={14} color="#2E6DA4" />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={styles.contactName}>{contact.name}</div>
                        <div style={styles.contactNumber}>{contact.number}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleEmergencyCall(contact.number)}
                      style={styles.callButton}
                    >
                      <PhoneCall size={12} style={{ marginRight: '4px' }} /> Call
                    </button>
                  </div>
                ))}
              </div>
              </div>
            </div>

            {/* Right: ACTIONS */}
            <div style={styles.card}>
              <div style={styles.cardHead}>
                <span style={styles.cardHeadIcon}><ClipboardList size={13} color="#2E6DA4" /></span>
                <span style={styles.cardHeadTitle}>Actions</span>
              </div>
              <div style={styles.cardBody}>
              <p style={{
                fontSize: '13px',
                color: '#4A5B6B',
                lineHeight: '1.6',
                margin: '0 0 14px 0',
                padding: '12px 14px',
                background: '#F7FAFD',
                border: '1px solid #E9EFF5',
                borderRadius: '12px'
              }}>
                {(isAcknowledged || isAssessmentSubmitted)
                  ? notificationData?.severity === 'Level 0 – Warning'
                    ? 'This warning alert has been acknowledged. View the details below or track the response from the incident dashboard.'
                    : 'This alert has been acknowledged and the incident situation assessment has been submitted. View the report below or track the response from the incident dashboard.'
                  : 'Review the alert details above, verify threat assessment readings, and acknowledge to begin response coordination.'}
              </p>

              <button
                onClick={() => setIsAssessmentModalOpen(true)}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: '700',
                  backgroundColor: (isAcknowledged || isAssessmentSubmitted) ? '#F0FDF4' : '#FFF7ED',
                  color: (isAcknowledged || isAssessmentSubmitted) ? '#166534' : '#C2410C',
                  border: (isAcknowledged || isAssessmentSubmitted) ? '1.5px solid #BBF7D0' : '1.5px solid #FFEDD5',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  fontFamily: "'Inter','Segoe UI',sans-serif",
                  marginTop: 'auto'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}><ClipboardList size={18} /></span>
                {(isAcknowledged || isAssessmentSubmitted)
                  ? notificationData?.severity === 'Level 0 – Warning'
                    ? 'View Acknowledge details'
                    : 'View Submitted Assessment Report'
                  : notificationData?.severity === 'Level 0 – Warning'
                    ? 'Acknowledge'
                    : 'Acknowledge & Incident Situation Assessment'}
              </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

// Layers Control Styles
const layersControlStyles = {
  container: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    zIndex: 1000,
  },
  button: {
    width: '40px',
    height: '40px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'all 0.2s ease',
    fontFamily: "'Inter','Segoe UI',sans-serif",
  },
  panel: {
    position: 'absolute',
    top: '50px',
    right: '0',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minWidth: '200px',
    overflow: 'hidden',
    fontFamily: "'Inter','Segoe UI',sans-serif",
  },
  panelHeader: {
    padding: '10px',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F0F6FB',
  },
  panelTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#0F2D42',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '0',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  layersList: {
    padding: '6px',
  },
  layerItem: {
    padding: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRadius: '6px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  layerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  layerName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
  },
};

// Component Styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FFFFFF',
    fontFamily: "'Inter','Segoe UI',sans-serif",
    paddingBottom: '24px'
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(42,101,145,.10)',
    padding: '16px 32px',
    boxShadow: '0 2px 20px rgba(15,45,66,.18)'
  },
  headerContent: {
    maxWidth: '1600px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  logo: {
    fontSize: '28px',
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#0F2D42',
    margin: 0
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    borderRadius: '6px',
  },
  statusLabel: {
    fontSize: '12px',
    color: '#7A9BB0',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  },
  statusValue: {
    fontSize: '13px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    backgroundColor: '#F0F6FB',
    border: '1px solid rgba(42,101,145,.10)',
    borderRadius: '10px',
    padding: '8px'
  },
  bottomBar: {
    color: 'white',
    padding: '13px 18px',
    margin: '18px 32px 0',
    borderRadius: '12px'
  },
  bottomBarContent: {
    maxWidth: '1600px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  bottomBarLeft: {},
  bottomBarText: {
    fontSize: '13.5px',
    fontWeight: '600',
    letterSpacing: '0.01em'
  },
  mainContent: {
    maxWidth: '1600px',
    margin: '24px auto',
    padding: '0 32px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px'
  },
  stackedLayout: {
    maxWidth: '1600px',
    margin: '16px auto',
    padding: '0 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  twoColRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    alignItems: 'stretch'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0',
    padding: '0'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '8px 22px',
    borderBottom: '1px solid #EDF1F6',
    borderRight: '1px solid #EDF1F6'
  },
  infoItemIcon: {
    fontSize: '18px',
    width: '30px',
    height: '30px',
    borderRadius: '9px',
    backgroundColor: '#EEF4FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '1px'
  },
  infoItemLabel: {
    fontSize: '10.5px',
    color: '#8095A8',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    marginBottom: '2px'
  },
  infoItemValue: {
    fontSize: '13.5px',
    color: '#12324A',
    fontWeight: '600',
    lineHeight: 1.45
  },
  sectionHeading: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#12324A',
    margin: '0 0 14px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    paddingLeft: '10px',
    borderLeft: '3px solid #3B82C4',
    lineHeight: 1.1
  },
  contactsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  contactCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    padding: '11px 12px',
    backgroundColor: '#F7FAFD',
    borderRadius: '12px',
    border: '1px solid #E9EFF5'
  },
  // Alert Card Styles (Left)
  alertCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '0',
    boxShadow: '0 1px 2px rgba(16,24,40,.04)',
    border: '1px solid #EEF1F5',
    overflow: 'hidden'
  },
  alertCardHeader: {
    padding: '9px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderBottom: '1px solid #EEF1F5',
    background: '#EBF4FA'
  },
  alertHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  },
  alertTitle: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1C4E72',
    letterSpacing: '0.02em'
  },
  severityBadge: {
    padding: '5px 12px',
    borderRadius: '999px',
    fontSize: '10.5px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
  },
  infoButton: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '2px solid #D1D5DB',
    backgroundColor: 'white',
    color: '#6B7280',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700'
  },
  regionSection: {
    padding: '16px 20px',
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(42,101,145,.05)'
  },
  regionLabel: {
    fontSize: '13px',
    color: '#7A9BB0',
    fontWeight: '500',
    marginRight: '8px'
  },
  regionValue: {
    fontSize: '14px',
    color: '#0F2D42',
    fontWeight: '600'
  },
  severityStatusRow: {
    padding: '14px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(42,101,145,.05)'
  },
  label: {
    fontSize: '13px',
    color: '#7A9BB0',
    fontWeight: '500',
    marginRight: '8px'
  },
  value: {
    fontSize: '13px',
    fontWeight: '600'
  },
  pendingBadge: {
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.5px'
  },
  measurementsSection: {
    padding: '20px',
    backgroundColor: 'white'
  },
  measurementRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '9px'
  },
  measurementLabel: {
    fontSize: '13px',
    color: '#3A5F78',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center'
  },
  measurementValue: {
    fontSize: '15px',
    color: '#12324A',
    fontWeight: '800'
  },
  progressBarContainer: {
    height: '8px',
    backgroundColor: '#EEF2F7',
    borderRadius: '999px',
    marginBottom: '18px',
    overflow: 'hidden'
  },
  progressBar: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.3s ease'
  },
  reportedTimeSection: {
    padding: '14px 20px',
    backgroundColor: '#FAFCFE',
    borderTop: '1px solid rgba(42,101,145,.05)',
    borderBottom: '1px solid rgba(42,101,145,.05)'
  },
  reportedTimeLabel: {
    fontSize: '12px',
    color: '#7A9BB0',
    fontWeight: '500',
    marginRight: '8px'
  },
  reportedTimeValue: {
    fontSize: '12px',
    color: '#0F2D42',
    fontWeight: '600'
  },
  triggerConditionsSection: {
    marginTop: '16px',
    padding: '14px 16px',
    backgroundColor: '#FFF8EC',
    border: '1px solid #F3E7CB',
    borderRadius: '12px',
    flex: 1
  },
  triggerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px'
  },
  triggerTitle: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#7A5A17',
    letterSpacing: '0.01em'
  },
  triggerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '9px'
  },
  triggerItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '9px',
    fontSize: '12px',
    color: '#4A5B6B',
    lineHeight: '1.5'
  },
  triggerBullet: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#D9A441',
    marginTop: '6px',
    flexShrink: 0
  },
  reportedTimeBottom: {
    padding: '14px 20px',
    backgroundColor: 'white',
    borderTop: '1px solid rgba(42,101,145,.05)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  reportedLabel: {
    fontSize: '12px',
    color: '#7A9BB0',
    fontWeight: '500'
  },
  reportedValue: {
    fontSize: '12px',
    color: '#0F2D42',
    fontWeight: '600',
    padding: '2px'

  },
  acknowledgeButton: {
    margin: '16px 20px',
    padding: '14px 20px',
    fontSize: '14px',
    fontWeight: '700',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  // Map and Right Panel
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '0',
    boxShadow: '0 1px 2px rgba(16,24,40,.04)',
    border: '1px solid #EEF1F5',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '11px 16px',
    background: '#EBF4FA',
    borderBottom: '1px solid #EEF1F5'
  },
  cardHeadIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '7px',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  cardHeadTitle: {
    fontSize: '12px',
    fontWeight: '800',
    color: '#2E6DA4',
    letterSpacing: '0.05em',
    textTransform: 'uppercase'
  },
  cardBody: {
    padding: '16px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px'
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0F2D42',
    margin: 0
  },
  badge: {
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600'
  },
  mapContainer: {
    height: '250px',
    width: '100%',
    marginBottom: '14px',
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid #E8EDF3',
    position: 'relative',
    zIndex: 0, // Changed to 0 to stay within parent stacking context
    isolation: 'isolate', // Added
    contain: 'layout style paint'
  },
  mapInfo: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  mapInfoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 12px',
    backgroundColor: '#F7FAFD',
    border: '1px solid #E9EFF5',
    borderRadius: '12px'
  },
  infoLabel: {
    fontSize: '10.5px',
    color: '#8095A8',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  infoValue: {
    fontSize: '13px',
    color: '#12324A',
    fontWeight: '700'
  },
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px'
  },
  statusItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px',
    backgroundColor: '#EBF4FA',
    borderRadius: '14px'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#0F2D42',
    margin: '0 0 12px 0'
  },
  contactsSection: {
    marginTop: '20px',
    padding: '14px',
    backgroundColor: '#EBF4FA',
    borderRadius: '14px'
  },
  contactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgba(42,101,145,.08)'
  },
  contactName: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#12324A'
  },
  contactNumber: {
    fontSize: '11px',
    color: '#8095A8',
    fontWeight: '600',
    marginTop: '2px'
  },
  callButton: {
    padding: '7px 14px',
    fontSize: '11.5px',
    fontWeight: '700',
    color: 'white',
    background: 'linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)',
    border: 'none',
    borderRadius: '9px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  formLabel: {
    fontSize: '13px',
    color: '#3A5F78',
    fontWeight: '600',
    fontFamily: "'Inter','Segoe UI',sans-serif"
  },
  formInput: {
    padding: '12px 14px',
    border: '1px solid rgba(42,101,145,.2)',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#0F2D42',
    backgroundColor: '#FAFCFE',
    fontFamily: "'Inter','Segoe UI',sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  formSectionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1C4E72',
    margin: '16px 0 8px 0',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(42,101,145,.1)',
  },
  submitBtn: {
    padding: '12px 24px',
    backgroundColor: '#E65100',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'opacity 0.2s',
  },
  readonlyGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px',
    backgroundColor: '#F8FBFE',
    borderRadius: '8px',
    border: '1px solid rgba(42,101,145,.08)'
  },
  readonlyValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#0F2D42'
  }
};

export default DisasterManagementUI;